import React from "react";
import { TooltipContentModule, TooltipContext } from "./tooltip-types";
import styles from "./Tooltip.module.css";
import { theme } from "antd";
import { CustomTooltip } from "src/components/CustomTooltip";
import { ValueFormatter } from "src/utils/formatters";

//A group is a record with children and measures

interface GroupStats {
  totalChildren: number;
  directChildren: number;
  measures: Array<{
    name: string;
    value: number;
    formattedValue: string;
  }>;
  distribution?: Array<{
    label: string;
    value: number;
    percentage: number;
  }>;
}

interface GroupRecord {
  children?: GroupRecord[];
  measures?: Record<string, number>;
  value?: string;
}

function calculateGroupStats(context: TooltipContext): GroupStats {
  const record = context.record as GroupRecord;
  const children = record?.children || [];
  const aggregations = context.aggregations || [];

  // Calculate basic stats
  const stats: GroupStats = {
    totalChildren: 0,
    directChildren: children.length,
    measures: [],
  };

  // Calculate total children (including nested)
  const countNestedChildren = (items: GroupRecord[]): number => {
    return items.reduce((count, item) => {
      return (
        count + 1 + (item.children ? countNestedChildren(item.children) : 0)
      );
    }, 0);
  };
  stats.totalChildren = countNestedChildren(children);

  // Calculate measures
  stats.measures = aggregations.map((agg) => {
    const measureKey = agg.key;
    const value = record.measures?.[measureKey] ?? 0;

    return {
      name: agg.title || agg.field,
      value,
      formattedValue: ValueFormatter.format(value, {
        field: agg.field,
      }),
    };
  });

  // Calculate distribution if we have children
  if (children.length > 0 && stats.measures.length > 0) {
    const mainMeasure = stats.measures[0];
    const total = mainMeasure.value;

    stats.distribution = children
      .map((child) => ({
        label: child.value || "",
        value: child.measures?.[mainMeasure.name] ?? 0,
        percentage: total
          ? ((child.measures?.[mainMeasure.name] ?? 0) / total) * 100
          : 0,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5); // Top 5 contributors
  }

  return stats;
}

export const GroupTooltipModule: TooltipContentModule = {
  type: "group",
  priority: 1,
  shouldRender: (context: TooltipContext) => {
    return context.type === "group";
  },
  render: (context: TooltipContext) => {
    const stats = calculateGroupStats(context);
    const { metadata } = context;

    const content = (
      <div className={styles.tooltip_container}>
        <div className={styles.tooltip_header}>Group Information</div>

        <div className={styles.tooltip_section}>
          <div className={styles.tooltip_row}>
            <div className={styles.path}>
              {metadata.path.map((step: string, index: number) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className={styles.pathSeparator}>›</span>}
                  <span className={styles.pathStep}>{step}</span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className={styles.tooltip_divider}></div>
          {/* Children Stats Section */}
          <div className={styles.tooltip_section}>
            <div className={styles.section_title}>Stats</div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Direct Children</div>
              <span className={styles.tooltip_value}>
                {stats.directChildren}
              </span>
            </div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Total Descendants</div>
              <span className={styles.tooltip_value}>
                {stats.totalChildren}
              </span>
            </div>
          </div>
        </div>

        {/* Measures Summary Section */}
        {stats.measures.length > 0 && (
          <div className={styles.section}>
            <div className={styles.section_title}>Measures</div>
            {stats.measures.map((measure) => (
              <div key={measure.name} className={styles.tooltip_row}>
                <span className={styles.tooltip_label}>{measure.name}</span>
                <span className={styles.tooltip_value}>
                  {measure.formattedValue}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    return (
      <CustomTooltip content={content} placement="right" size="medium">
        {context.children}
      </CustomTooltip>
    );
  },
};
